/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Operations (contract section 14, mockup admin-13, "Build as drawn"): the five operations metrics from GET
 * /v1/admin/pulse/operations, in the owner's order: cost per outcome (domain × outcome, with the most expensive unhelpful
 * intents), speed (p95 time to first word and turn by surface or domain; the ten slowest tools), the approval funnel per
 * tool, model and provider health (its table scrolls sideways: six columns do not fit beside the funnel), and knowledge
 * gaps (asks that found nothing, and World IQ asks that look like company questions, with a scrubbed sample).
 */
import { useState } from 'react';
import { Coins, Cpu, Timer } from 'lucide-react';
import { api } from '../../lib/api';
import {
  PULSE_DOMAINS, cacheHitPct, costTotal, countRows, domainLabel, funnelRates, keyed, knowledgeGapRows, msText, pctText, pulseExportHref, pulseHref, sortBy, surfaceLabel, toggleSort, usdText,
  type PulseModelRow, type PulseOperations as OperationsData, type SortState,
} from '../../lib/pulse';
import { navigate } from '../../shell/router';
import { Drawer, EmptyState, Grid, PageHeader, Panel, Segmented, Stack, StatusChip, Table, Tag, type Column } from '../../ui';
import { count, tokens } from '../../ui/format';
import { DEFAULT_FILTERS, ExportButton, Facts, FilterBar, PageLink, PulseError, filterParams, useLoad, type PulseFilters } from './common';

interface CostRow { domain: string; done: number; gaps: number; answered: number; couldNot: number; asks: number }
interface SpeedRow { key: string; n: number; ttftP95: number | null; durP95: number | null }
type FunnelRow = OperationsData['funnel'][string] & { key: string };
type ModelRow = PulseModelRow & { key: string };
type GapRow = ReturnType<typeof knowledgeGapRows>[number];

function costRows(cost: OperationsData['cost']): CostRow[] {
  const order = [...PULSE_DOMAINS, ...Object.keys(cost).filter((domain) => !(PULSE_DOMAINS as readonly string[]).includes(domain))];
  return order.filter((domain) => cost[domain]).map((domain) => {
    const cells = cost[domain] ?? {};
    const usd = (outcome: keyof typeof cells) => cells[outcome]?.usd ?? 0;
    const asks = Object.values(cells).reduce((sum, cell) => sum + (cell?.asks ?? 0), 0);
    return { domain, done: usd('done'), gaps: usd('done-with-gaps'), answered: usd('answered'), couldNot: usd('could-not'), asks };
  });
}

const money = (value: number) => (value > 0 ? usdText(value) : '—');

export function PulseOperations() {
  const [filters, setFilters] = useState<PulseFilters>({ ...DEFAULT_FILTERS, days: 30 });
  const [speedBy, setSpeedBy] = useState<'surface' | 'domain'>('surface');
  const [costSort, setCostSort] = useState<SortState>({ key: 'couldNot', direction: 'desc' });
  const [funnelSort, setFunnelSort] = useState<SortState>({ key: 'proposed', direction: 'desc' });
  const [modelSort, setModelSort] = useState<SortState>({ key: 'calls', direction: 'desc' });
  const [gapSort, setGapSort] = useState<SortState>({ key: 'asks', direction: 'desc' });
  const [model, setModel] = useState<ModelRow | null>(null);
  const params = filterParams(filters);
  const { load, reload, reloading } = useLoad(() => api.pulseOperations(params), JSON.stringify(params));
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';

  const cost = data ? sortBy(costRows(data.cost), costSort, (row, key) => (key === 'domain' ? domainLabel(row.domain) : (row as unknown as Record<string, number>)[key])) : [];
  const speedRows: SpeedRow[] = data ? keyed(data.speed[speedBy]).map((row) => ({ key: row.key, n: row.n, ttftP95: row.ttftP95, durP95: row.durP95 })).sort((a, b) => b.n - a.n) : [];
  const slowest: SpeedRow[] = data ? keyed(data.speed.tool).map((row) => ({ key: row.key, n: row.n, ttftP95: row.ttftP95, durP95: row.durP95 })).sort((a, b) => (b.durP95 ?? 0) - (a.durP95 ?? 0)).slice(0, 10) : [];
  const funnel = data ? sortBy(keyed(data.funnel), funnelSort, (row, key) => (key === 'tool' ? row.key : key === 'confirmMsP95' ? row.confirmMsP95 : key === 'expiredPct' ? funnelRates(row).expiredPct : key === 'cancelledPct' ? funnelRates(row).cancelledPct : (row as unknown as Record<string, number>)[key])) : [];
  const models = data ? sortBy(keyed(data.models), modelSort, (row, key) => (key === 'model' ? row.key : key === 'errors' ? row.failed : key === 'cacheHit' ? cacheHitPct(row) : (row as unknown as Record<string, number>)[key])) : [];
  const gaps = data ? sortBy(knowledgeGapRows(data.knowledge), gapSort, (row, key) => (key === 'intent' ? row.intent : key === 'kind' ? row.kind : row.asks)) : [];

  const costColumns: Array<Column<CostRow>> = [
    { key: 'domain', header: 'Domain', sortable: true, cell: (row) => <PageLink href={pulseHref.domain(row.domain)}>{domainLabel(row.domain)}</PageLink> },
    { key: 'done', header: 'Done', numeric: true, sortable: true, cell: (row) => money(row.done) },
    { key: 'gaps', header: 'With gaps', numeric: true, sortable: true, cell: (row) => money(row.gaps) },
    { key: 'answered', header: 'Answered', numeric: true, sortable: true, cell: (row) => money(row.answered) },
    { key: 'couldNot', header: 'Could not', numeric: true, sortable: true, cell: (row) => money(row.couldNot) },
  ];
  const speedColumns: Array<Column<SpeedRow>> = [
    { key: 'key', header: speedBy === 'surface' ? 'By surface' : 'By domain', cell: (row) => (speedBy === 'surface' ? surfaceLabel(row.key) : domainLabel(row.key)) },
    { key: 'ttft', header: 'First word', numeric: true, cell: (row) => msText(row.ttftP95) },
    { key: 'dur', header: 'Turn', numeric: true, cell: (row) => msText(row.durP95) },
  ];
  const slowestColumns: Array<Column<SpeedRow>> = [
    { key: 'tool', header: 'Slowest tools', mono: true, cell: (row) => <PageLink href={pulseHref.subject(`tool:${row.key}`)}>{row.key}</PageLink> },
    { key: 'p95', header: 'p95', numeric: true, cell: (row) => msText(row.durP95) },
    { key: 'n', header: 'Calls', numeric: true, cell: (row) => count(row.n) },
  ];
  const funnelColumns: Array<Column<FunnelRow>> = [
    { key: 'tool', header: 'Tool', mono: true, sortable: true, cell: (row) => row.key },
    { key: 'proposed', header: 'Proposed', numeric: true, sortable: true, cell: (row) => count(row.proposed) },
    { key: 'confirmed', header: 'Confirmed', numeric: true, sortable: true, cell: (row) => count(row.confirmed) },
    { key: 'done', header: 'Done', numeric: true, sortable: true, cell: (row) => count(row.done) },
    { key: 'confirmMsP95', header: 'Confirm p95', numeric: true, sortable: true, cell: (row) => msText(row.confirmMsP95) },
    { key: 'expiredPct', header: 'Expired', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => pctText(funnelRates(row).expiredPct, 1) },
    { key: 'cancelledPct', header: 'Cancelled', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => pctText(funnelRates(row).cancelledPct, 1) },
  ];
  const modelColumns: Array<Column<ModelRow>> = [
    { key: 'model', header: 'Model', sortable: true, cell: (row) => row.key },
    { key: 'calls', header: 'Calls', numeric: true, sortable: true, cell: (row) => count(row.calls) },
    { key: 'fallbacks', header: 'Fallbacks', numeric: true, sortable: true, cell: (row) => (row.fallbacks > 0 ? count(row.fallbacks) : '—') },
    { key: 'errors', header: 'Errors', numeric: true, sortable: true, cell: (row) => { const top = countRows(row.providerErrors)[0]; return <span className="ui-nowrap">{count(row.failed)}{top ? <> <Tag title="The most frequent provider error code and how often">{`${top.key} ×${count(top.count)}`}</Tag></> : null}</span>; } },
    { key: 'outputCut', header: 'Cut off', numeric: true, sortable: true, cell: (row) => count(row.outputCut) },
    { key: 'cacheHit', header: 'Cache hit', numeric: true, sortable: true, cell: (row) => pctText(cacheHitPct(row)) },
  ];
  const gapColumns: Array<Column<GapRow>> = [
    { key: 'intent', header: 'Intent', sortable: true, cell: (row) => <PageLink href={pulseHref.intent(row.intentKey)}>{row.intent}</PageLink> },
    { key: 'kind', header: 'Kind', sortable: true, cell: (row) => (row.kind === 'no-source' ? <StatusChip tone="warn">no source</StatusChip> : <StatusChip tone="info">World IQ, company</StatusChip>) },
    { key: 'asks', header: 'Asks', numeric: true, sortable: true, cell: (row) => count(row.asks) },
    { key: 'sample', header: 'Sample (scrubbed)', hideOnPhone: true, cell: (row) => (row.sample ? <q>{row.sample}</q> : <span className="ui-quiet">—</span>) },
  ];

  const total = data ? costTotal(data.cost) : 0;
  const knowledge = data?.knowledge;

  return (
    <>
      <PageHeader
        meta={<span>What Gyro&apos;s answers cost, how fast they are, how approvals flow, how the models behave, and where knowledge is missing.</span>}
        actions={(
          <>
            <FilterBar filters={filters} onChange={setFilters} />
            <ExportButton href={pulseExportHref('operations', params)} />
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          {load.state === 'failed' ? <PulseError title="The operations metrics could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : null}
          <Grid cols={2}>
            <Panel
              flush
              title="Cost per outcome"
              description="Model spend by domain and outcome"
              actions={<span className="ui-quiet">{filters.days} days · {usdText(total)} total</span>}
              footer={data ? (
                <span>Most expensive unhelpful intents: {data.unhelpfulIntents?.length ? (
                  <span className="ui-chips">{data.unhelpfulIntents.slice(0, 6).map((row) => <PageLink key={row.intentKey} href={pulseHref.intent(row.intentKey)}><StatusChip tone="neutral" plain title={`${domainLabel(row.domain)} · ${count(row.asks)} asks`}>{row.intent} · {usdText(row.usd)}</StatusChip></PageLink>)}</span>
                ) : <span className="ui-quiet">{data.unhelpfulIntents ? 'none in this range' : 'not reported by this service version'}</span>}</span>
              ) : undefined}
            >
              <Table
                label="Cost per outcome"
                columns={costColumns}
                rows={cost}
                rowKey={(row) => row.domain}
                loading={loading}
                skeletonRows={5}
                sort={{ key: costSort.key, direction: costSort.direction, onSort: (key) => setCostSort(toggleSort(costSort, key, key !== 'domain')) }}
                total={data ? { domain: 'Total', done: money(cost.reduce((s, r) => s + r.done, 0)), gaps: money(cost.reduce((s, r) => s + r.gaps, 0)), answered: money(cost.reduce((s, r) => s + r.answered, 0)), couldNot: money(cost.reduce((s, r) => s + r.couldNot, 0)) } : undefined}
                empty={<EmptyState compact icon={Coins} title="No spend in this range">Costs appear here as Pulse records asks.</EmptyState>}
              />
            </Panel>
            <Panel
              flush
              title="Speed"
              description="p95 time to first word and turn length"
              actions={<Segmented label="Speed by" value={speedBy} onChange={setSpeedBy} options={[{ value: 'surface', label: 'Surface' }, { value: 'domain', label: 'Domain' }]} />}
            >
              <Table label={speedBy === 'surface' ? 'Speed by surface' : 'Speed by domain'} columns={speedColumns} rows={speedRows} rowKey={(row) => row.key} loading={loading} skeletonRows={3} stickyHeader={false}
                empty={<EmptyState compact icon={Timer} title="No turns in this range">Speed appears here as Pulse records asks.</EmptyState>} />
              {slowest.length || loading ? <Table label="Slowest tools" columns={slowestColumns} rows={slowest} rowKey={(row) => row.key} loading={loading} skeletonRows={4} stickyHeader={false} /> : null}
            </Panel>
          </Grid>
          <Grid cols={2}>
            <Panel flush title="Approval funnel" description="Proposed → confirmed → done, per tool" actions={<span className="ui-quiet">per tool · {filters.days} days</span>}>
              <Table
                label="Approval funnel"
                columns={funnelColumns}
                rows={funnel}
                rowKey={(row) => row.key}
                loading={loading}
                skeletonRows={4}
                onRowClick={(row) => navigate(pulseHref.subject(`tool:${row.key}`))}
                sort={{ key: funnelSort.key, direction: funnelSort.direction, onSort: (key) => setFunnelSort(toggleSort(funnelSort, key, key !== 'tool')) }}
                empty={<EmptyState compact icon={Timer} title="No proposed actions in this range">The funnel fills as Gyro proposes actions people confirm.</EmptyState>}
              />
            </Panel>
            <Panel flush title="Model and provider health" description="Per model: calls, fallbacks, provider errors, output cut-offs, cache hits">
              <div className="pulse-scroll">
                <Table
                  label="Model and provider health"
                  columns={modelColumns}
                  rows={models}
                  rowKey={(row) => row.key}
                  loading={loading}
                  skeletonRows={4}
                  onRowClick={(row) => setModel(row)}
                  sort={{ key: modelSort.key, direction: modelSort.direction, onSort: (key) => setModelSort(toggleSort(modelSort, key, key !== 'model')) }}
                  empty={<EmptyState compact icon={Cpu} title="No model calls in this range">Model health appears here as Pulse records asks.</EmptyState>}
                />
              </div>
            </Panel>
          </Grid>
          <Panel
            flush
            title="Knowledge gaps"
            description={knowledge ? `${count(knowledge.noSource)} of ${count(knowledge.workAsks)} company-knowledge asks found nothing · ${count(knowledge.likelyCompanyInWorld)} of ${count(knowledge.worldAsks)} World IQ asks look like company questions` : 'Asks whose retrieval found nothing, and World IQ asks that look like company questions'}
            actions={<span className="ui-quiet">asks that found nothing, and World IQ asks that look like company questions</span>}
          >
            <Table
              label="Knowledge gaps"
              columns={gapColumns}
              rows={gaps}
              rowKey={(row) => `${row.kind}:${row.intentKey}`}
              loading={loading}
              skeletonRows={3}
              sort={{ key: gapSort.key, direction: gapSort.direction, onSort: (key) => setGapSort(toggleSort(gapSort, key, key === 'asks')) }}
              empty={<EmptyState compact icon={Coins} title={knowledge && !knowledge.noSourceIntents && !knowledge.likelyCompanyIntents ? 'Intents not reported' : 'No knowledge gaps in this range'}>{knowledge && !knowledge.noSourceIntents && !knowledge.likelyCompanyIntents ? 'This service version sends the counts without the intents behind them.' : 'Asks that found no source, and World IQ asks about the company, appear here by intent.'}</EmptyState>}
            />
          </Panel>
        </Stack>
      </div>
      <Drawer open={model !== null} onClose={() => setModel(null)} title={model?.key ?? 'Model'} description={model ? `${count(model.calls)} calls in the last ${filters.days} days` : undefined}>
        {model ? (
          <Facts rows={[
            ['Failed calls', `${count(model.failed)} · ${count(model.fallbacks)} fell back to another model`],
            ['Provider errors', countRows(model.providerErrors).length ? <ul className="ui-list">{countRows(model.providerErrors).map((row) => <li key={row.key}><code>{row.key}</code> × {count(row.count)}</li>)}</ul> : 'none'],
            ['Output cut off', `${count(model.outputCut)} answers stopped at the output limit`],
            ['Tokens', `${tokens(model.inputTokens)} in · ${tokens(model.outputTokens)} out · cache ${tokens(model.cacheRead)} read, ${tokens(model.cacheWrite)} written`],
            ['Cache hit', pctText(cacheHitPct(model))],
          ]} />
        ) : null}
      </Drawer>
    </>
  );
}
